import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import { createContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { LaunchConfigurationsDTO } from "../../../store/launch-configurations/models/dto";
import { LaunchesActions } from "../../../store/launches/actions";
import { StatusEnum } from "../../../store/users/models/internal";
import { Users } from "../../../store/users/reducer";

export type ConfigContext = {
  config: LaunchConfigurationsDTO.Responses.GetSingle.T;
  state: ReturnType<typeof useConfigState>;
};

export const ConfigContext = createContext<ConfigContext>(
  (undefined as any) as ConfigContext
);

export function useConfigState(
  item: LaunchConfigurationsDTO.Responses.GetSingle.T
) {
  const dispatch = useDispatch();

  const user = useFetchable(Users.current);

  const [innerUserExists, setUserExists] = useState(false);

  // state for emailVerified
  const [emailVerified, setEmailVerified] = useState<string>();

  // state for loggedIn
  const [userLoggedIn, setUserLoggedIn] = useState<boolean>();

  // state for userKycStatus
  const [userKycStatus, setUserKycStatus] = useState<typeof StatusEnum.T>();

  // state for dirty
  const [dirty, setDirty] = useState(false);

  const userDetails = useFetchable(Users.current);

  useEffect(() => {
    if (isFetchableSuccess(userDetails)) {
      setUserLoggedIn(true);
      setUserKycStatus(userDetails.data.kycStatus);
    }
  }, [userDetails, setUserLoggedIn, setUserKycStatus]);

  const { isLoading, error, data } = useVisitorData({
    extendedResult: true,
  });

  useEffect(() => {
    dispatch(
      LaunchesActions.setParticipationForm({
        id: item.id,
        data: {
          user: {
            emailAddress: emailVerified,
            alreadyProcessed: userKycStatus === "VERIFIED",
          },
        },
      })
    );
  }, [userKycStatus, dispatch, item.id, emailVerified]);

  return {
    emailVerified,
    dirty,
    setEmailVerified,
    setDirty,
    userLoggedIn,
    userKycStatus,
    setUserLoggedIn,
    setUserKycStatus,
    fingerprint: useMemo(() => (isLoading || error ? undefined : data), [
      isLoading,
      error,
      data,
    ]),
    setUserExists,
    userExists: useMemo(() => isFetchableSuccess(user) || innerUserExists, [
      user,
      innerUserExists,
    ]),
  };
}
